import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Paper,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import api from "../../services/api";
import { toast } from "react-toastify";
import {
  ALL_DISK_CLEANUP_EXTENSIONS,
  DISK_CLEANUP_EXTENSION_GROUPS,
  formatExtensionLabel,
  normalizeSelectedExtensions,
} from "../../constants/diskCleanupExtensions";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(3),
    },
  },
  embeddedContent: {
    padding: theme.spacing(2, 0),
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  extensionGroup: {
    marginBottom: theme.spacing(1.5),
  },
  groupTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  extensionGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  selectActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    flexWrap: "wrap",
  },
}));

const DiskCleanupForm = ({
  companyId,
  companyName,
  diskUsage,
  warningBanner,
  embedded = false,
  onStarted,
  onCancel,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDarkMode = theme.palette.type === "dark";

  const [daysToKeep, setDaysToKeep] = useState(30);
  const [selectedExtensions, setSelectedExtensions] = useState([
    ...ALL_DISK_CLEANUP_EXTENSIONS,
  ]);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        const { data } = await api.get(`/companies/${companyId}/disk-cleanup/options`);
        if (data.daysToKeep) {
          setDaysToKeep(parseInt(data.daysToKeep, 10) || 30);
        }
        if (data.selectedExtensions?.length) {
          setSelectedExtensions(normalizeSelectedExtensions(data.selectedExtensions));
        } else {
          setSelectedExtensions([...ALL_DISK_CLEANUP_EXTENSIONS]);
        }
      } catch (error) {
        setSelectedExtensions([...ALL_DISK_CLEANUP_EXTENSIONS]);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [companyId]);

  const toggleExtension = (ext) => {
    setSelectedExtensions((prev) =>
      prev.includes(ext) ? prev.filter((item) => item !== ext) : [...prev, ext]
    );
  };

  const toggleGroup = (extensions, selectAll) => {
    setSelectedExtensions((prev) => {
      if (selectAll) {
        return [...new Set([...prev, ...extensions])];
      }
      return prev.filter((ext) => !extensions.includes(ext));
    });
  };

  const handleSelectAll = () => setSelectedExtensions([...ALL_DISK_CLEANUP_EXTENSIONS]);
  const handleDeselectAll = () => setSelectedExtensions([]);

  const handleStartCleanup = async () => {
    if (!daysToKeep || daysToKeep < 1 || daysToKeep > 365) {
      toast.error("O número de dias deve estar entre 1 e 365");
      return;
    }

    const extensions = normalizeSelectedExtensions(selectedExtensions);
    if (extensions.length === 0) {
      toast.error("Selecione pelo menos um tipo de arquivo para limpar");
      return;
    }

    setLoading(true);
    try {
      await api.post(`/companies/${companyId}/disk-cleanup`, {
        daysToKeep,
        extensions,
      });

      toast.success("Limpeza de disco iniciada! Acompanhe o progresso no canto da tela.");
      window.dispatchEvent(
        new CustomEvent("showCleanupProgress", { detail: { companyId } })
      );

      if (onStarted) {
        onStarted({ companyId, daysToKeep, extensions });
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.error || "Erro ao iniciar limpeza de disco";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box className={embedded ? classes.embeddedContent : undefined}>
      {warningBanner}

      {(companyName || diskUsage) && (
        <Paper
          elevation={0}
          className={classes.paper}
          style={{
            backgroundColor: isDarkMode ? "rgba(33, 150, 243, 0.12)" : "#e3f2fd",
            border: isDarkMode
              ? "1px solid rgba(33, 150, 243, 0.4)"
              : "1px solid #2196f3",
          }}
        >
          {companyName && (
            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
              Empresa: {companyName} (ID {companyId})
            </Typography>
          )}
          {diskUsage && (
            <Typography variant="body2">
              Uso atual: <strong>{diskUsage.formattedDiskUsage || "0 B"}</strong> de{" "}
              <strong>{diskUsage.formattedDiskQuota || "0 B"}</strong> (
              {diskUsage.percentage || 0}%)
            </Typography>
          )}
        </Paper>
      )}

      <Typography variant="body2" paragraph>
        Defina quantos dias de arquivos deseja manter e selecione os tipos que podem ser
        removidos. Apenas arquivos mais antigos que o período informado serão apagados.
      </Typography>

      <TextField
        label="Dias para manter arquivos"
        type="number"
        value={daysToKeep}
        onChange={(e) => setDaysToKeep(parseInt(e.target.value, 10) || 30)}
        inputProps={{ min: 1, max: 365 }}
        fullWidth
        variant="outlined"
        helperText="Arquivos com mais de X dias serão removidos (1-365 dias)"
      />

      <Box mt={2}>
        <Typography variant="subtitle2" gutterBottom>
          Tipos de arquivo para limpar
        </Typography>
        <Box className={classes.selectActions}>
          <Button size="small" onClick={handleSelectAll}>
            Selecionar todos
          </Button>
          <Button size="small" onClick={handleDeselectAll}>
            Desmarcar todos
          </Button>
        </Box>

        {DISK_CLEANUP_EXTENSION_GROUPS.map((group) => {
          const allSelected = group.extensions.every((ext) =>
            selectedExtensions.includes(ext)
          );
          const someSelected = group.extensions.some((ext) =>
            selectedExtensions.includes(ext)
          );

          return (
            <Box key={group.label} className={classes.extensionGroup}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" className={classes.groupTitle}>
                  {group.label}
                </Typography>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => toggleGroup(group.extensions, !allSelected)}
                >
                  {allSelected ? "Desmarcar grupo" : "Marcar grupo"}
                </Button>
              </Box>
              <FormGroup row className={classes.extensionGrid}>
                {group.extensions.map((ext) => (
                  <FormControlLabel
                    key={ext}
                    control={
                      <Checkbox
                        checked={selectedExtensions.includes(ext)}
                        onChange={() => toggleExtension(ext)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={formatExtensionLabel(ext)}
                  />
                ))}
              </FormGroup>
              {!someSelected && (
                <Typography variant="caption" color="textSecondary">
                  Nenhum tipo deste grupo selecionado
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Box className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleStartCleanup}
          disabled={loading || selectedExtensions.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Iniciar Limpeza
        </Button>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
        )}
      </Box>
    </Box>
  );
};

const DiskCleanupModal = ({
  open,
  onClose,
  companyId,
  companyName,
  diskUsage,
  warningBanner,
  onStarted,
}) => {
  const classes = useStyles();

  const handleStarted = useCallback(
    (data) => {
      if (onStarted) {
        onStarted(data);
      }
      if (onClose) {
        onClose();
      }
    },
    [onClose, onStarted]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Limpeza de Disco</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {companyId ? (
          <DiskCleanupForm
            companyId={companyId}
            companyName={companyName}
            diskUsage={diskUsage}
            warningBanner={warningBanner}
            onStarted={handleStarted}
            onCancel={onClose}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { DiskCleanupForm };
export default DiskCleanupModal;
